import { useContext, useEffect, useMemo, useState } from "react";
import { useReactFlow } from "reactflow";
import { CommonModal } from "../../../components";
import { DrawingContext } from "../../../components/drawing-tool/DrawingTool";
import { checkNodeModalValidation } from "../../../components/drawing-tool/utils";
import { CommonSideModal } from "../../../components/ui/widget/CommonModal";
import { ENABLE_MAPPING_INGREDIENT_UI_REDESIGN } from "../../../config/constants";
import { nodeType } from "../../../config/drawingConstants";
import { MappingSourceType } from "../../../config/enums/MappingSourceType.enum";
import { errorView } from "../../../helpers/ErrorHandler";
import useWindowDimensions from "../../../hooks/windowsSize";
import { ClientBase } from "./ClientBase";
import { CultivarBase } from "./CultivarBase";
import { ExternalStorageBase } from "./ExternalStorageBase";
import { FarmGatePackagingBase } from "./FarmGate/Packaging/FarmGatePackagingBase";
import { FarmGateProcessBase } from "./FarmGate/Processing/FarmGateProcessBase";
import { InternalStorageBase } from "./FarmGate/Storage/InternalStorageBase";
import { InternalTransportBase } from "./FarmGate/Transport/InternalTransportBase";
import IngredientDetailsView from "./Ingredient";
import { ProcessGatePackagingBase } from "./ProcessGate/Packaging/ProcessGatePackagingBase";
import { ProcessGateProcessingBase } from "./ProcessGate/Processing/ProcessGateProcessBase";
import { ProcessInternalStorageBase } from "./ProcessGate/Storage/InternalStorageBase";
import { ProcessInternalTransportBase } from "./ProcessGate/Transport/InternalTransportBase";
import { TransportBase } from "./TransportBase";
import "./UpdateNodeModal.scss";

const PACKAGING_SUBTITLE =
  "Please add information about how you package products at each level following the GS1 guidelines. Use the image below as a reference.";

interface UpdateNodeModalProps {
  sourceId: string;
  ownerId: string | null | undefined;
  loading?: boolean;
}

const EXTERNAL_TRANSPORT_SUBTITLE =
  "Register freight legs moved outside your own sites — vehicle, route, weight and distance.";

const EXTERNAL_STORAGE_SUBTITLE =
  "Register storage outside your own sites — facility, temperature, weight, storage duration and utilization.";

const INTERNAL_STORAGE_SUBTITLE =
  "Register storage at your own sites — facility, temperature, weight, storage duration and utilization.";

const INTERNAL_TRANSPORT_SUBTITLE =
  "Register how harvest moves from the field(s) to the facility — vehicle, average distance and weight.";

const INGREDIENTS_SUBTITLE =
  "Select the ingredient and supplier used in this product. Ingredient details are loaded from the supplier record.";

const UpdateNodeModal = (props: UpdateNodeModalProps) => {
  const [modalDescription, setModalDescription] = useState("");
  const { ownerId, sourceId } = props;
  const drawingContext = useContext(DrawingContext);
  const { nodeItemId, chartNodes, chartEdges, mappingSourceType, setNodeItem, lockStatus } =
    drawingContext;
  const reactFlow = useReactFlow();

  const nodeItem = useMemo(() => {
    // Support both string ids (on open) and full node objects (after save/update)
    const nodeId = typeof nodeItemId === "string" ? nodeItemId : nodeItemId?.id || "";
    return nodeId ? reactFlow.getNode(nodeId) : undefined;
  }, [nodeItemId, reactFlow]);

  const height = useWindowDimensions();
  const createOrUpdateStatus =
    mappingSourceType === MappingSourceType.ingredient && !nodeItem?.data.reference
      ? "Create"
      : "Update";

  const modalValidation = useMemo(() => {
    if (!nodeItem) return null;
    return checkNodeModalValidation(nodeItem, chartNodes, chartEdges, sourceId, ownerId);
  }, [nodeItem, chartNodes, chartEdges, sourceId, ownerId]);

  useEffect(() => {
    if (modalValidation && !modalValidation.status) {
      setNodeItem(null);
      errorView(modalValidation.errorMessage);
    }
  }, [modalValidation, setNodeItem]);

  const modalData = (modalValidation?.data || {}) as {
    requiresIncomingConnection?: boolean;
    hasIncomingConnection?: boolean;
    [key: string]: any;
  };

  // The Cultivar no longer requires an incoming Ingredient connection (a farmer
  // can set the produce type directly on the Cultivar form instead), so it is
  // excluded from the connection lock the same way Ingredient nodes are.
  const isExcludedFromConnectionLock =
    nodeItem?.data?.icon === nodeType.Ingredients || nodeItem?.data?.icon === nodeType.Cultivar;

  const isTransportNode =
    nodeItem?.data?.icon === nodeType.Transportation ||
    nodeItem?.data?.icon === nodeType.FarmGateInternalTransport ||
    nodeItem?.data?.icon === nodeType.ProcessGateInternalTransport;

  const isExternalStorageNode = nodeItem?.data?.icon === nodeType.Warehouse;

  const isInternalStorageNode =
    nodeItem?.data?.icon === nodeType.FarmGateInternalStorage ||
    nodeItem?.data?.icon === nodeType.ProcessGateInternalStorage;

  const isPackagingNode =
    nodeItem?.data?.icon === nodeType.FarmGatePacking ||
    nodeItem?.data?.icon === nodeType.ProcessGatePacking;

  const isProcessNode =
    nodeItem?.data?.icon === nodeType.FarmGateProcess ||
    nodeItem?.data?.icon === nodeType.ProcessGateProcessing;

  // These node types lock their own fields off `lockStatus` internally (independent of
  // the ancestor pointer-events/opacity wrapper below), so the wrapper's blanket dimming
  // is redundant for them and only serves to incorrectly gray out their Delete button too.
  const managesOwnConnectionLockStyling =
    isTransportNode ||
    isExternalStorageNode ||
    isInternalStorageNode ||
    isPackagingNode ||
    isProcessNode;

  const isConnectionLocked =
    !isTransportNode &&
    !isExternalStorageNode &&
    !isExcludedFromConnectionLock &&
    !!modalData.requiresIncomingConnection &&
    !modalData.hasIncomingConnection;

  const isFormLocked = !!lockStatus || isConnectionLocked;

  const formContextValue = useMemo(
    () => ({
      ...drawingContext,
      // Transport and external storage drawers manage disconnected state internally — keep
      // global lock only so Delete stays available when unconnected.
      lockStatus: isTransportNode || isExternalStorageNode ? !!lockStatus : isFormLocked,
      // Real lock only (ignores connection-lock) so Delete stays available on
      // unconnected nodes even when their fields are still grayed out.
      deleteLockStatus: !!lockStatus
    }),
    [drawingContext, isExternalStorageNode, isFormLocked, isTransportNode, lockStatus]
  );

  const nodeTypeContent = useMemo(() => {
    if (!modalValidation?.status || !nodeItem) return null;

    const data = modalValidation.data;
    const nodeContentMap: { [key: string]: JSX.Element } = {
      [nodeType.Ingredients]: <IngredientDetailsView modalData={nodeItem} />,
      [nodeType.Client]: <ClientBase modalData={data} />,
      [nodeType.FarmGateInternalTransport]: <InternalTransportBase modalData={data} />,
      [nodeType.Transportation]: <TransportBase modalData={data} />,
      [nodeType.FarmGateInternalStorage]: <InternalStorageBase modalData={data} />,
      [nodeType.FarmGateProcess]: <FarmGateProcessBase modalData={data} />,
      [nodeType.FarmGatePacking]: <FarmGatePackagingBase modalData={data} />,
      [nodeType.Warehouse]: <ExternalStorageBase />,
      [nodeType.Cultivar]: <CultivarBase modalData={data} />,
      [nodeType.ProcessGateInternalTransport]: <ProcessInternalTransportBase modalData={data} />,
      [nodeType.ProcessGateInternalStorage]: <ProcessInternalStorageBase modalData={data} />,
      [nodeType.ProcessGatePacking]: <ProcessGatePackagingBase modalData={data} />,
      [nodeType.ProcessGateProcessing]: <ProcessGateProcessingBase modalData={data} />
    };

    return nodeContentMap[nodeItem?.data.icon] || null;
  }, [modalValidation, nodeItem]);

  const nodeTypeHeader = useMemo(() => {
    const nodeHeaderMap: { [key: string]: string } = {
      [nodeType.Client]: "Client",
      [nodeType.Cultivar]: "Cultivar"
    };

    // The redesigned Ingredients editor is a side drawer (nodeTypeHeaderDetails);
    // while ENABLE_MAPPING_INGREDIENT_UI_REDESIGN is off it keeps the legacy
    // centered CommonModal it has always used.
    if (!ENABLE_MAPPING_INGREDIENT_UI_REDESIGN) {
      nodeHeaderMap[nodeType.Ingredients] = "Ingredients";
    }

    return nodeHeaderMap[nodeItem?.data.icon] || "";
  }, [nodeItem]);

  const nodeTypeHeaderDetails = useMemo(() => {
    const nodeHeaderMap: { [key: string]: string } = {
      [nodeType.Transportation]: "External Transportation",
      [nodeType.Warehouse]: "External Warehouse",
      [nodeType.FarmGateInternalStorage]: "Internal Storage",
      [nodeType.ProcessGateInternalStorage]: "Internal Storage",
      [nodeType.FarmGateInternalTransport]: "Internal Transportation",
      [nodeType.ProcessGateInternalTransport]: "Internal Transportation",
      [nodeType.Cultivar]: "Cultivar Configuration",
      [nodeType.FarmGateProcess]: "Processing Information",
      [nodeType.ProcessGateProcessing]: "Processing Configuration",
      [nodeType.FarmGatePacking]: "Packaging Information",
      [nodeType.ProcessGatePacking]: "Packaging Information"
    };

    if (ENABLE_MAPPING_INGREDIENT_UI_REDESIGN) {
      nodeHeaderMap[nodeType.Ingredients] = "Ingredients";
    }

    return nodeHeaderMap[nodeItem?.data.icon] || "";
  }, [nodeItem]);

  const nodeTypeSubHeaderDetails = useMemo(() => {
    const nodeHeaderMap: { [key: string]: string } = {
      [nodeType.Transportation]: EXTERNAL_TRANSPORT_SUBTITLE,
      [nodeType.Warehouse]: EXTERNAL_STORAGE_SUBTITLE,
      [nodeType.FarmGateInternalStorage]: INTERNAL_STORAGE_SUBTITLE,
      [nodeType.ProcessGateInternalStorage]: INTERNAL_STORAGE_SUBTITLE,
      [nodeType.FarmGateInternalTransport]: INTERNAL_TRANSPORT_SUBTITLE,
      [nodeType.ProcessGateInternalTransport]: INTERNAL_TRANSPORT_SUBTITLE,
      [nodeType.Cultivar]:
        "Configure farm gate and cultivation details. Save the farm gate configuration to persist all section data.",
      [nodeType.ProcessGateProcessing]:
        "Configure the process and its sub-sections. Save the main config first to unlock sub-sections.",
      [nodeType.FarmGateProcess]:
        "Configure the process and its sub-sections. Save the main config first to unlock sub-sections.",
      [nodeType.FarmGatePacking]: PACKAGING_SUBTITLE,
      [nodeType.ProcessGatePacking]: PACKAGING_SUBTITLE
    };

    if (ENABLE_MAPPING_INGREDIENT_UI_REDESIGN) {
      nodeHeaderMap[nodeType.Ingredients] = INGREDIENTS_SUBTITLE;
    }

    return nodeHeaderMap[nodeItem?.data.icon] || "";
  }, [nodeItem]);

  const showConnectionLockOverlay = isConnectionLocked && !managesOwnConnectionLockStyling;

  const modalBody = (
    <DrawingContext.Provider value={formContextValue}>
      <div
        className={
          showConnectionLockOverlay
            ? "tw-pointer-events-none tw-opacity-60 tw-select-none"
            : undefined
        }
        aria-disabled={showConnectionLockOverlay || undefined}
      >
        {nodeTypeContent}
      </div>
    </DrawingContext.Provider>
  );

  if (!modalValidation?.status) {
    return null;
  }

  return nodeTypeHeader ? (
    <CommonModal
      modalOpenState={!!nodeItem}
      modalCloseState={() => {
        setNodeItem(null);
      }}
      centered={true}
      title={`${createOrUpdateStatus} ${nodeTypeHeader}`}
      style={height}
      modalDescription={modalDescription}
    >
      {modalBody}
    </CommonModal>
  ) : nodeTypeHeaderDetails ? (
    <CommonSideModal
      isOpen={!!nodeItem}
      onClose={() => setNodeItem(null)}
      title={`${createOrUpdateStatus} ${nodeTypeHeaderDetails}`}
      subtitle={`${nodeTypeSubHeaderDetails}`}
    >
      {modalBody}
    </CommonSideModal>
  ) : null;
};

export default UpdateNodeModal;
